import { ERROR_CODES, type SponsoredWorkspaceView } from "@closer/shared";
import { Badge, Button, Card, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState, type UiState } from "../../lib/uiState";

/**
 * SPONSORLU MÜVEKKİL ALANI (CLOS-865 · sunucusu CLOS-175 `[122]`).
 *
 * ### ⚠ BU EKRAN AYLARDIR YOKTU
 *
 * Sunucu eksiksizdi ve testliydi (`POST/GET /api/clients/:id/workspace`, kapasite
 * kontrolü transactional + advisory kilitli) ama `apps/web/src` altında `sponsored`
 * kelimesi bile geçmiyordu. CLOS-175 metni arayüzü "hard blocker değil" diye ertelemiş,
 * kimse dönmemişti. Sonuç: ürünün ana zinciri arayüzden tamamlanamıyordu — "backend
 * bitti, ekran unutuldu" sınıfının üçüncü vakası (CLOS-863).
 *
 * ### ⚠ DURUM ALANI YOK — ve bu bilinçli bir EKSİKLİK
 *
 * Kart bir "durum kartı" istiyor ama *Aktif / Askıda / Sona ermiş* ayrımı bugün MODELDE
 * YOK: `sponsored_client_workspaces` böyle bir kolon taşımıyor ve `SponsoredWorkspaceView`
 * böyle bir alan döndürmüyor. Uydurulmuş bir durum rozeti, olmayan bir ürün davranışını
 * varmış gibi gösterirdi — kullanıcı "askıya al" arar, bulamaz. Karar CLOS-867'de;
 * gelene kadar kart iki hâl biliyor: **kurulmuş** ve **kurulmamış**.
 *
 * ### ⚠ "ALANA GİT" DÜĞMESİ DE YOK — kartta isteniyordu, ölçüm engelledi
 *
 * `sponsoredWorkspace.ts` alan kurarken sponsor için ÜYELİK OLUŞTURMUYOR (ölçüldü: dosyada
 * tek bir `memberships` yazması yok) ve şemanın kendi notu diyor ki *"sponsor ödeme
 * ilişkisi görme/yönetme hakkı sağlamaz."* Yani düğme kullanıcıyı üyesi olmadığı bir
 * çalışma alanına götürürdü: ya alan seçicide hiç görünmez ya 403 alırdı. Erişilemeyen
 * bir yere açılan kapı, olmayan kapıdan kötüdür — kullanıcıya var olmayan bir hak
 * vaat eder.
 *
 * Bunun yerine alanın KİMLİĞİ gösteriliyor: sponsor için anlamlı olan, alanın kurulduğu
 * ve hangi kayda karşılık geldiği.
 */
/**
 * Alanın varlığını okuyan ORTAK sorgu.
 *
 * ⚠ Hem bu kart hem temsilci davet bölümü aynı cevabı istiyor (davet alanın varlığına
 * bağlı). Ayrı anahtarlarla iki sorgu açsaydık aynı veri iki kez çekilir ve biri
 * tazelenip öteki eskiyebilirdi — kart kurulu görünürken davet hâlâ kilitli kalırdı.
 */
export function useClientWorkspace(clientId: string) {
  const { activeTenantId } = useAuth();

  const anahtar = activeTenantId
    ? tenantKey(activeTenantId, "client-workspace", clientId)
    : ["t", "yok", "client-workspace", clientId];

  const sorgu = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (
        await api<SponsoredWorkspaceView | null>(`/api/clients/${clientId}/workspace`, {
          tenantId: activeTenantId ?? undefined,
        })
      ).data,
  });

  return { sorgu, anahtar };
}

export function ClientWorkspace({
  clientId,
  arsivde,
  clientType = "organization",
}: {
  clientId: string;
  arsivde: boolean;
  /** Gerçek kişi müvekkilde temsilci kavramı yok (CLOS-1122 R1.2): kopya ona göre. */
  clientType?: "individual" | "organization";
}) {
  const { activeTenantId, can } = useAuth();
  const { preferences } = usePreferences();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { sorgu, anahtar } = useClientWorkspace(clientId);

  /**
   * KOTA REDDİNİN KALICI İZİ (CLOS-870).
   *
   * ⚠ Bildirim TEK BAŞINA yetmiyordu: 4 saniye sonra kendi kendine kapanıyor ve geriye
   * hiçbir iz kalmıyordu. Kullanıcı o an başka yere bakıyorsa ekranda gördüğü şey
   * "düğmeye bastım, hiçbir şey olmadı" — kart hâlâ *Kurulmamış*, düğme hâlâ yerinde.
   * Sunucu doğru cevabı veriyor ama istemci onu görünür bir yerde tutmuyordu.
   *
   * ⚠ KALICILIK YALNIZ `QUOTA_EXCEEDED`'a veriliyor. Kataloğun kendi tanımı ayrımı
   * yapıyor: kota "zamanla çözülmez; plan değişmelidir". Geçici bir çakışma ya da
   * bağlantı hatası kartta asılı kalsaydı, kullanıcı kotası dolmadığı hâlde dolduğunu
   * sanardı — bildirim onlar için doğru ömür.
   */
  const [kotaReddi, setKotaReddi] = useState<UiState | null>(null);

  const olustur = useMutation({
    mutationFn: () =>
      api<SponsoredWorkspaceView>(`/api/clients/${clientId}/workspace`, {
        method: "POST",
        tenantId: activeTenantId ?? undefined,
      }),
    /** Yeni deneme eski reddi taşımaz: kapanmış bir hak dolu görünmeye devam etmesin. */
    onMutate: () => setKotaReddi(null),
    onSuccess: () => {
      setKotaReddi(null);
      toast.success("Müvekkil Alanı kuruldu");
      void queryClient.invalidateQueries({ queryKey: anahtar });
    },
    /**
     * ⚠ KAPASİTE REDDİ GÖRÜNÜR OLMALI (kartın 3. maddesi).
     *
     * Sunucu `QUOTA_EXCEEDED` ile zaten anlaşılır bir cümle döndürüyor ("hakkınız dolu
     * (2/2)" ya da "aboneliğiniz izin vermiyor"). O cümle OLDUĞU GİBİ gösteriliyor;
     * burada yeniden yazsaydık, sunucudaki gerçek sebeple ekrandaki açıklama bir gün
     * ayrışırdı (kriter 4: UI string uydurmaz).
     *
     * ⚠ Dallanma `code` üzerinden — metnin üzerinden DEĞİL (`apps/web/CLAUDE.md`).
     * Sunucunun cümlesi bir gün değişse bile kalıcılık kararı yerinde kalır.
     */
    onError: (hata: unknown) => {
      if (!(hata instanceof ApiRequestError)) {
        setKotaReddi(null);
        toast.error("Müvekkil Alanı kurulamadı");
        return;
      }
      setKotaReddi(
        hata.code === ERROR_CODES.QUOTA_EXCEEDED
          ? /**
             * Metin ve başlık ORTAK kapıdan geçiyor (`resolveErrorState`): sayfa kendi
             * cümlesini yazmıyor, güvenlik süzgeci de atlanmıyor. `idempotent: false`
             * çünkü POST kör tekrarlanamaz — kapı bu yüzden "Yeniden dene" sunmuyor.
             */
            resolveErrorState(hata, { idempotent: false, existenceIsSecret: false })
          : null,
      );
      toast.error(hata.message);
    },
  });

  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          error: {
            title: "Müvekkil Alanı bilgisi yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  const alan = sorgu.data ?? null;

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold text-fg-heading">Müvekkil Alanı</h2>
            <p className="text-sm text-fg-muted">
              Müvekkilin kendi çalışma alanı. Kuruluş masrafını sizin aboneliğiniz karşılar; alanın
              içeriğine erişiminiz olmaz.
            </p>
          </div>

          {sorgu.isPending ? null : alan ? (
            <Badge tone="success">Kurulmuş</Badge>
          ) : (
            <Badge tone="neutral">Kurulmamış</Badge>
          )}
        </div>

        {sorgu.isPending ? (
          <div role="status" aria-label="Müvekkil Alanı yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : hataDurumu ? (
          <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
        ) : alan ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-fg-soft">Alan kimliği</dt>
            <dd data-testid="alan-kimligi" className="font-mono text-xs text-fg">
              {alan.workspaceTenantId}
            </dd>
            <dt className="text-fg-soft">Kuran</dt>
            <dd className="text-fg">{alan.createdByName}</dd>
            <dt className="text-fg-soft">Kuruluş</dt>
            <dd className="text-fg">{formatDate(alan.createdAt, preferences.locale)}</dd>
          </dl>
        ) : (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-fg-muted">
              {clientType === "individual"
                ? "Bu müvekkil için henüz bir alan kurulmadı. Müvekkil Closer'a davet edildiğinde alan, davet kabul edilince kendiliğinden kurulur; dilerseniz şimdi de kurabilirsiniz."
                : "Bu müvekkil için henüz bir alan kurulmadı. Temsilci davet edebilmek için önce alanı kurmanız gerekir."}
            </p>

            {/*
              ⚠ Yetki `client.workspace` — `client.edit` DEĞİL. Alan kurmak abonelik hakkı
              tüketiyor ve müvekkile ürün içinde bir yer açıyor; iletişim bilgisi düzelten
              herkesin yapabileceği bir iş değil (sunucu da aynı anahtarı soruyor).

              ⚠ Arşivli müvekkilde gizli DEĞİL, yalnız çizilmiyor: sunucu da 409 veriyor.
            */}
            {!arsivde && can("client.workspace") ? (
              <Button size="sm" disabled={olustur.isPending} onClick={() => olustur.mutate()}>
                Müvekkil Alanı oluştur
              </Button>
            ) : null}
          </div>
        )}

        {/*
          ⚠ Bildirime EK olarak, onun yerine değil: anlık uyarı hızlıdır, kalıcı uyarı
          iz bırakır. Sunum `StateView` — kartın okuma hatası da aynı bileşenle çiziliyor;
          burada ayrı bir kutu çizseydik aynı kartta iki farklı hata dili olurdu.
        */}
        {kotaReddi ? (
          <div data-testid="kota-uyarisi">
            <StateView state={kotaReddi} />
          </div>
        ) : null}
      </div>
    </Card>
  );
}
